/**
 * SupportChatWidget - Loads Freshchat + UserPilot scripts for authenticated users.
 *
 * Mirrors `_Layout.cshtml` lines 147-298. Mounts vendor scripts via `useEffect`,
 * calls `fcWidget.init()` so the chat icon renders, sets user properties, and
 * identifies the current user to UserPilot.
 *
 * Renders no DOM of its own. Safe to mount multiple times — guards against
 * duplicate script tags and duplicate init.
 */

import { useEffect, useRef } from 'react';
import { useUserContextData } from '../UserContextProvider/UserContextProvider.store';

const FRESHCHAT_SRC = 'https://wchat.freshchat.com/js/widget.js';
const USERPILOT_SRC = 'https://js.userpilot.io/sdk/latest.js';
const USERPILOT_TOKEN = 'NX-81e1048b';
const FRESHCHAT_TOKEN = 'baa07bc5-6e06-482d-8437-200e23f18f4e';
const FRESHCHAT_HOST = 'https://wchat.freshchat.com';
const FRESHCHAT_BG_IMAGE =
  'https://wchat.freshchat.com/assets/images/texture_background_1-bdc7191884a15871ed640bcb0635e7e7.png';

// Locale mapping mirrors `_Layout.cshtml` switch (lines 250-273).
const FRESHCHAT_LOCALE_MAP: Record<string, string> = {
  'zh-CN': 'zh-HANS',
  'hr-HR': 'hr',
  'cs-CZ': 'cs',
  'da-DK': 'da',
  'nl-BE': 'nl',
  'et-EE': 'et',
  'fi-FI': 'fi',
  'fr-FR': 'fr',
  'de-DE': 'de',
  'el-GR': 'el',
  'hu-HU': 'hu',
  'it-IT': 'it',
  'lv-LV': 'lv',
  'pl-PL': 'pl',
  'pt-PT': 'pt',
  'ro-RO': 'ro',
  'sk-SK': 'sk',
  'sl-SI': 'sl',
  'es-ES': 'es',
  'sv-SE': 'sv',
};

interface FcWidgetUser {
  setFirstName: (v: string) => void;
  setLastName: (v: string) => void;
  setEmail: (v: string) => void;
  setPhone: (v: string) => void;
  setProperties: (props: Record<string, unknown>) => void;
}

interface FcWidget {
  init: (config: Record<string, unknown>) => void;
  setExternalId: (id: string) => void;
  user: FcWidgetUser;
}

declare global {
  interface Window {
    userpilot?: {
      identify: (userId: string, traits: Record<string, unknown>) => void;
    };
    userpilotSettings?: { token: string };
    fcWidget?: FcWidget;
    appCulture?: string;
  }
}

const ensureScript = (src: string): HTMLScriptElement => {
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
  if (existing) return existing;
  const el = document.createElement('script');
  el.src = src;
  el.async = true;
  document.head.appendChild(el);
  return el;
};

const pollUntil = (
  predicate: () => boolean,
  action: () => void,
  maxAttempts = 50,
  intervalMs = 100
): (() => void) => {
  let cancelled = false;
  let timer: number | undefined;
  let attempts = 0;
  const tick = () => {
    if (cancelled) return;
    if (predicate()) {
      action();
      return;
    }
    if (attempts++ < maxAttempts) {
      timer = window.setTimeout(tick, intervalMs);
    }
  };
  tick();
  return () => {
    cancelled = true;
    if (timer !== undefined) window.clearTimeout(timer);
  };
};

export interface SupportChatWidgetProps {
  /** Override token for testing */
  userpilotToken?: string;
  /** Override Freshchat token for testing */
  freshchatToken?: string;
}

export const SupportChatWidget: React.FC<SupportChatWidgetProps> = ({
  userpilotToken = USERPILOT_TOKEN,
  freshchatToken = FRESHCHAT_TOKEN,
}) => {
  const user = useUserContextData();
  const identifiedRef = useRef<string | null>(null);
  const fcInitRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.userId) return;

    const userIdStr = String(user.userId);
    window.userpilotSettings = { token: userpilotToken };
    ensureScript(FRESHCHAT_SRC);
    ensureScript(USERPILOT_SRC);

    const cancellers: Array<() => void> = [];

    // Freshchat: init once SDK loaded, then set user properties so chat icon renders.
    if (fcInitRef.current !== userIdStr) {
      const culture = window.appCulture ?? 'en-US';
      const locale = FRESHCHAT_LOCALE_MAP[culture] ?? 'en';
      const externalId = user.email || userIdStr;
      const appName = user.tenant?.name ? `${user.tenant.name} Support` : 'ECGrid Support';

      cancellers.push(
        pollUntil(
          () => Boolean(window.fcWidget?.init),
          () => {
            const fc = window.fcWidget!;
            fc.init({
              token: freshchatToken,
              host: FRESHCHAT_HOST,
              externalId,
              locale,
              config: {
                cssNames: { widget: 'fc_frame' },
                showFAQOnOpen: false,
                hideFAQ: true,
                headerProperty: {
                  backgroundColor: '#014f6e',
                  foregroundColor: '#ffffff',
                  direction: 'ltr',
                  appName,
                  backgroundImage: FRESHCHAT_BG_IMAGE,
                },
              },
            });
            fc.setExternalId(externalId);
            fc.user.setFirstName(user.firstName ?? '');
            fc.user.setLastName(user.lastName ?? '');
            fc.user.setEmail(user.email ?? '');
            fc.user.setPhone(user.phone ?? '');
            fc.user.setProperties({ plan: 'Blossom', status: 'Active' });
            fcInitRef.current = userIdStr;
          }
        )
      );
    }

    // UserPilot: identify once SDK loaded.
    if (identifiedRef.current !== userIdStr) {
      cancellers.push(
        pollUntil(
          () => Boolean(window.userpilot?.identify),
          () => {
            window.userpilot!.identify(userIdStr, {
              name: user.userName ?? '',
              first_name: user.firstName ?? '',
              last_name: user.lastName ?? '',
              role: user.roles?.[0] ?? '',
              network: user.tenant?.networkId ?? '',
              mailbox: user.tenant?.mailboxId ?? '',
              portal_title: user.tenant?.name ?? '',
              company: {
                id: `${user.tenant?.networkId ?? ''} : ${user.tenant?.mailboxId ?? ''}`,
                name: user.tenant?.networkName ?? '',
              },
            });
            identifiedRef.current = userIdStr;
          }
        )
      );
    }

    return () => {
      for (const c of cancellers) c();
    };
  }, [user, userpilotToken, freshchatToken]);

  return null;
};

SupportChatWidget.displayName = 'SupportChatWidget';

export default SupportChatWidget;
